import React from "react";
import { Field, getIn, useFormikContext } from "formik";
import NumberFormat from "react-number-format";
import {
  AttachMoney,
  Percent,
  Cancel,
  Search,
  People,
  Map,
} from "@mui/icons-material";
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { useGlobalData } from "../../../../provider/GlobalDataProvider";
import { isEmpty } from "lodash";
import BootstrapTooltip from "../BootstrapTooltip";

export const FormTextField = ({
  label = "",
  name = "",
  secondName = "",
  variant = "outlined",
  size = "small",
  margin = "normal",
  searchButton = false,
  clearButton = false,
  assignmeButton = false,
  mapsButton = false,
  readonly = false,
  currencyField = false,
  currencyCode = "",
  fullDecimals = false,
  percentageField = false,
  quantityField = false,
  iconField = false,
  iconOnStart = "",
  iconOnStartColor = "",
  iconOnStartTooltip = "",
  iconOnEnd = "",
  iconOnEndColor = "",
  iconOnEndTooltip = "",
  style = {},
  clearFields = [],
  onClearFields,
  onSearchClick,
  onAssignmeClick,
  onMapsClick,
  onChangeValue,
  onBlur,
  autoFocus = false,
  minRows,
  multiline = false,
  upperCase = "none",
}) => {
  const { companyProfile } = useGlobalData();
  const {
    handleBlur,
    setFieldValue,
    touched,
    errors,
    values,
  } = useFormikContext();
  let disabled = false;
  if (currencyCode === "") {
    currencyCode = companyProfile.currency.currencyCode;
  }

  if (readonly) {
    disabled = true;
  }

  if (searchButton || assignmeButton || mapsButton) {
    disabled = false;
  }
  let fieldName = "";
  let fieldValue = "";
  if (secondName !== "") {
    fieldValue = values[name][secondName];
    fieldName = `${name}[${secondName}]`;
  } else {
    fieldValue = values[name];
    fieldName = name;
  }
  const touchedProperty = getIn(touched, fieldName);
  const errorsProperty = getIn(errors, fieldName);
  const inputProps = {
    endAdornment:
      searchButton ||
      clearButton ||
      assignmeButton ||
      mapsButton ||
      percentageField ||
      iconField ? (
        <InputAdornment position="end">
          {percentageField ? "%" : null}
          {iconField && iconOnEnd !== "" ? (
            <React.Fragment>
              {iconOnEndTooltip !== "" ? (
                <BootstrapTooltip title={iconOnEndTooltip}>
                  <i
                    className={`${iconOnEnd}${
                      iconOnEndColor !== "" ? ` text-${iconOnEndColor}` : ``
                    }`}
                  ></i>
                </BootstrapTooltip>
              ) : (
                <i
                  className={`${iconOnEnd}${
                    iconOnEndColor !== "" ? ` text-${iconOnEndColor}` : ``
                  }`}
                ></i>
              )}
            </React.Fragment>
          ) : null}
          {assignmeButton ? (
            <BootstrapTooltip title="Assign To Myself">
              <IconButton
                type="button"
                aria-label="AssignToMyself"
                onClick={onAssignmeClick}
                edge="end"
              >
                <People />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {mapsButton ? (
            <BootstrapTooltip title="Pin Address From Maps">
              <IconButton
                type="button"
                aria-label="PinAddressFromMaps"
                onClick={onMapsClick}
                edge="end"
              >
                <Map />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {searchButton ? (
            <BootstrapTooltip title="Search">
              <IconButton
                type="button"
                aria-label="Search"
                onClick={onSearchClick}
                edge="end"
              >
                <Search />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {clearButton ? (
            <BootstrapTooltip title="Clear">
              <IconButton
                type="button"
                aria-label="Clear"
                onClick={() => {
                  clearFields.forEach((field, index) => {
                    setFieldValue(field.field, field.clearValue);
                  });
                  if (onClearFields) {
                    onClearFields();
                  }
                }}
                edge="end"
              >
                <Cancel />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
        </InputAdornment>
      ) : null,
    startAdornment:
      currencyField || iconField ? (
        <InputAdornment position="start">
          {iconField && iconOnStart !== "" ? (
            <React.Fragment>
              {iconOnStartTooltip !== "" ? (
                <BootstrapTooltip title={iconOnStartTooltip}>
                  <i
                    className={`${iconOnStart}${
                      iconOnStartColor !== "" ? ` text-${iconOnStartColor}` : ``
                    }`}
                  ></i>
                </BootstrapTooltip>
              ) : (
                <i
                  className={`${iconOnStart}${
                    iconOnStartColor !== "" ? ` text-${iconOnStartColor}` : ``
                  }`}
                ></i>
              )}
            </React.Fragment>
          ) : null}
          {currencyField ? `${currencyCode}` : null}
        </InputAdornment>
      ) : null,
    readOnly: readonly,
  };

  if (currencyField || percentageField) {
    if (fullDecimals === true) {
      inputProps.inputComponent = CurrencyFormatFullDecimals;
    } else {
      inputProps.inputComponent = CurrencyFormat;
    }
  }
  return (
    <>
      <TextField
        id={fieldName}
        type={quantityField ? "number" : "text"}
        label={label}
        value={fieldValue ?? ""}
        variant={variant}
        disabled={disabled}
        size={size}
        margin={margin}
        style={{ width: "100%", ...style }}
        onBlur={(e) => {
          handleBlur(e);
          if (onBlur) {
            onBlur(e.target.value);
          }
        }}
        onChange={(e) => {
          if (quantityField) {
            if (isEmpty(e.target.value)) {
              e.target.value = 0;
            }
            e.target.value = parseFloat(e.target.value);
          }
          if (upperCase === "uppercase") {
            e.target.value = e.target.value.toUpperCase();
          }
          setFieldValue(fieldName, e.target.value);
          if (onChangeValue) {
            onChangeValue(e.target.value);
          }
        }}
        helperText={touchedProperty && errorsProperty}
        error={Boolean(touchedProperty && errorsProperty)}
        InputProps={inputProps}
        inputProps={
          currencyField
            ? {
                style: {
                  textAlign: "right",
                },
              }
            : upperCase === "uppercase"
            ? {
                style: {
                  textTransform: upperCase,
                },
              }
            : {}
        }
        InputLabelProps={{ shrink: true }}
        autoFocus={autoFocus}
        multiline={multiline}
        minRows={minRows}
      />
    </>
  );
};

export const FormDateField = ({
  name = "",
  label = "",
  secondName = "",
  margin = "normal",
  disablePast = false,
  disableFuture = false,
  readonly = false,
}) => {
  const { touched, errors } = useFormikContext();
  let disabled = false;

  if (readonly) {
    disabled = true;
  }
  let fieldName = "";
  if (secondName !== "") {
    fieldName = `${name}[${secondName}]`;
  } else {
    fieldName = name;
  }
  const touchedProperty = getIn(touched, fieldName);
  const errorsProperty = getIn(errors, fieldName);
  const inputProps = {
    readOnly: true,
  };
  return (
    <Field
      name={name}
      label={label}
      margin={margin}
      helperText={touchedProperty && errorsProperty}
      error={Boolean(touchedProperty && errorsProperty)}
      disablePast={disablePast}
      disableFuture={disableFuture}
      component={DateFormat}
      InputProps={inputProps}
      disabled={disabled}
      size="small"
    />
  );
};

export const FormArrayDateField = ({
  arrayPropName = "",
  index = 0,
  propName = "",
  label = "",
  disablePast = false,
  disableFuture = false,
  readonly = false,
}) => {
  const { touched, errors } = useFormikContext();
  let disabled = false;

  if (readonly) {
    disabled = true;
  }

  const fieldName = `${arrayPropName}[${index}].${propName}`;
  const touchedProperty = getIn(touched, fieldName);
  const errorsProperty = getIn(errors, fieldName);
  const inputProps = {
    readOnly: true,
  };
  return (
    <Field
      name={fieldName}
      label={label}
      margin="none"
      helperText={touchedProperty && errorsProperty}
      error={Boolean(touchedProperty && errorsProperty)}
      disablePast={disablePast}
      disableFuture={disableFuture}
      component={DateFormat}
      InputProps={inputProps}
      disabled={disabled}
      size="small"
    />
  );
};

export const FormArrayTextField = ({
  arrayPropName = "",
  arrayPropName2 = "",
  index = 0,
  propName = "",
  label = "",
  variant = "outlined",
  size = "small",
  margin = "normal",
  searchButton = false,
  clearButton = false,
  assignmeButton = false,
  mapsButton = false,
  percentButton = false,
  amountButton = false,
  readonly = false,
  currencyField = false,
  currencyCode = "",
  percentageField = false,
  quantityField = false,
  iconField = false,
  iconOnStart = "",
  iconOnStartColor = "",
  iconOnStartTooltip = "",
  iconOnEnd = "",
  iconOnEndColor = "",
  iconOnEndTooltip = "",
  placeholder = "",
  style = {},
  clearFields = [],
  onClearFields,
  onSearchClick,
  onAssignmeClick,
  onMapsClick,
  onAmountClick,
  onPercentClick,
  onBlur,
  onChangeValue,
  autoFocus = false,
  value,
}) => {
  const { companyProfile } = useGlobalData();
  const {
    handleBlur,
    setFieldValue,
    touched,
    errors,
    values,
  } = useFormikContext();
  let disabled = false;
  if (currencyCode === "") {
    currencyCode = companyProfile.currency.currencyCode;
  }
  if (readonly) {
    disabled = true;
  }

  if (searchButton || assignmeButton || mapsButton) {
    disabled = false;
  }
  let fieldValue = "";
  let fieldName = "";
  if (arrayPropName2 !== "") {
    fieldValue = values[arrayPropName][arrayPropName2][index][propName];
    fieldName = `${arrayPropName}[${arrayPropName2}][${index}].${propName}`;
  } else if (arrayPropName !== "") {
    fieldValue = values[arrayPropName][index][propName];
    fieldName = `${arrayPropName}[${index}].${propName}`;
  } else {
    fieldValue = values[index][propName];
    fieldName = `[${index}].${propName}`;
  }
  const touchedProperty = getIn(touched, fieldName);
  const errorsProperty = getIn(errors, fieldName);
  const inputProps = {
    endAdornment:
      searchButton ||
      clearButton ||
      assignmeButton ||
      mapsButton ||
      amountButton ||
      percentButton ||
      percentageField ||
      iconField ? (
        <InputAdornment position="end">
          {percentageField ? "%" : null}
          {iconField && iconOnEnd !== "" ? (
            <React.Fragment>
              {iconOnEndTooltip !== "" ? (
                <BootstrapTooltip title={iconOnEndTooltip}>
                  <i
                    className={`${iconOnEnd}${
                      iconOnEndColor !== "" ? ` text-${iconOnEndColor}` : ``
                    }`}
                  ></i>
                </BootstrapTooltip>
              ) : (
                <i
                  className={`${iconOnEnd}${
                    iconOnEndColor !== "" ? ` text-${iconOnEndColor}` : ``
                  }`}
                ></i>
              )}
            </React.Fragment>
          ) : null}
          {assignmeButton ? (
            <BootstrapTooltip title="Assign To Myself">
              <IconButton
                type="button"
                aria-label="AssignToMyself"
                onClick={onAssignmeClick}
                edge="end"
              >
                <People />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {mapsButton ? (
            <BootstrapTooltip title="Pin Address From Maps">
              <IconButton
                type="button"
                aria-label="PinAddressFromMaps"
                onClick={onMapsClick}
                edge="end"
              >
                <Map />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {searchButton ? (
            <BootstrapTooltip title="Search">
              <IconButton
                type="button"
                aria-label="Search"
                onClick={onSearchClick}
                edge="end"
              >
                <Search />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {clearButton ? (
            <BootstrapTooltip title="Clear">
              <IconButton
                type="button"
                aria-label="Clear"
                onClick={() => {
                  clearFields.forEach((field, index) => {
                    setFieldValue(field.field, field.clearValue);
                  });
                  if (onClearFields) {
                    onClearFields();
                  }
                }}
                edge="end"
              >
                <Cancel />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {percentButton ? (
            <BootstrapTooltip title="Input By Percentage">
              <IconButton
                type="button"
                aria-label="InputByPercentage"
                onClick={onPercentClick}
                edge="end"
              >
                <Percent />
              </IconButton>
            </BootstrapTooltip>
          ) : null}

          {amountButton ? (
            <BootstrapTooltip title="Input By Amount">
              <IconButton
                type="button"
                aria-label="InputByAmount"
                onClick={onAmountClick}
                edge="end"
              >
                <AttachMoney />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
        </InputAdornment>
      ) : null,
    startAdornment:
      currencyField || iconField ? (
        <InputAdornment position="start">
          {iconField && iconOnStart !== "" ? (
            <React.Fragment>
              {iconOnStartTooltip !== "" ? (
                <BootstrapTooltip title={iconOnStartTooltip}>
                  <i
                    className={`${iconOnStart}${
                      iconOnStartColor !== "" ? ` text-${iconOnStartColor}` : ``
                    }`}
                  ></i>
                </BootstrapTooltip>
              ) : (
                <i
                  className={`${iconOnStart}${
                    iconOnStartColor !== "" ? ` text-${iconOnStartColor}` : ``
                  }`}
                ></i>
              )}
            </React.Fragment>
          ) : null}
          {currencyField ? `${currencyCode}` : null}
        </InputAdornment>
      ) : null,
    autoFocus,
    readOnly: readonly,
  };
  if (currencyField || percentageField) {
    inputProps.inputComponent = CurrencyFormat;
  }
  return (
    <>
      <TextField
        id={fieldName}
        type={quantityField ? "number" : "text"}
        label={label}
        value={value ? value : fieldValue ?? ""}
        variant={variant}
        disabled={disabled}
        size={size}
        margin={margin}
        style={{ width: "100%", ...style }}
        onBlur={(e) => {
          handleBlur(e);
          if (onBlur) {
            onBlur(e.target.value);
          }
        }}
        onChange={(e) => {
          if (quantityField) {
            if (isEmpty(e.target.value)) {
              e.target.value = 0;
            }
            e.target.value = parseFloat(e.target.value);
          }
          setFieldValue(fieldName, e.target.value);
          if (onChangeValue) {
            onChangeValue(e.target.value);
          }
        }}
        helperText={touchedProperty && errorsProperty}
        error={Boolean(touchedProperty && errorsProperty)}
        InputProps={inputProps}
        InputLabelProps={{ shrink: true }}
        inputProps={
          currencyField
            ? {
                style: {
                  textAlign: "right",
                },
              }
            : {}
        }
        placeholder={placeholder}
      />
    </>
  );
};

export const FormArrayTextField2 = ({
  arrayPropName = "",
  arrayPropName2 = "",
  index = 0,
  index2 = 0,
  propName = "",
  label = "",
  variant = "outlined",
  size = "small",
  margin = "normal",
  searchButton = false,
  clearButton = false,
  assignmeButton = false,
  mapsButton = false,
  percentButton = false,
  amountButton = false,
  readonly = false,
  currencyField = false,
  currencyCode = "",
  percentageField = false,
  quantityField = false,
  iconField = false,
  iconOnStart = "",
  iconOnStartColor = "",
  iconOnStartTooltip = "",
  iconOnEnd = "",
  iconOnEndColor = "",
  iconOnEndTooltip = "",
  placeholder = "",
  style = {},
  clearFields = [],
  onClearFields,
  onSearchClick,
  onAssignmeClick,
  onMapsClick,
  onAmountClick,
  onPercentClick,
  onBlur,
  onChangeValue,
  autoFocus = false,
  value,
}) => {
  const { companyProfile } = useGlobalData();
  const {
    handleBlur,
    setFieldValue,
    touched,
    errors,
    values,
  } = useFormikContext();
  let disabled = false;
  if (currencyCode === "") {
    currencyCode = companyProfile.currency.currencyCode;
  }
  if (readonly) {
    disabled = true;
  }

  if (searchButton || assignmeButton || mapsButton) {
    disabled = false;
  }
  let fieldValue = "";
  let fieldName = "";
  if (arrayPropName2 !== "") {
    fieldValue = values[arrayPropName][index][arrayPropName2][index2][propName];
    fieldName = `${arrayPropName}[${index}][${arrayPropName2}][${index2}].${propName}`;
  } else if (arrayPropName !== "") {
    fieldValue = values[arrayPropName][index][propName];
    fieldName = `${arrayPropName}[${index}].${propName}`;
  } else {
    fieldValue = values[index][propName];
    fieldName = `[${index}].${propName}`;
  }
  const touchedProperty = getIn(touched, fieldName);
  const errorsProperty = getIn(errors, fieldName);
  const inputProps = {
    endAdornment:
      searchButton ||
      clearButton ||
      assignmeButton ||
      mapsButton ||
      amountButton ||
      percentButton ||
      percentageField ||
      iconField ? (
        <InputAdornment position="end">
          {percentageField ? "%" : null}
          {iconField && iconOnEnd !== "" ? (
            <React.Fragment>
              {iconOnEndTooltip !== "" ? (
                <BootstrapTooltip title={iconOnEndTooltip}>
                  <i
                    className={`${iconOnEnd}${
                      iconOnEndColor !== "" ? ` text-${iconOnEndColor}` : ``
                    }`}
                  ></i>
                </BootstrapTooltip>
              ) : (
                <i
                  className={`${iconOnEnd}${
                    iconOnEndColor !== "" ? ` text-${iconOnEndColor}` : ``
                  }`}
                ></i>
              )}
            </React.Fragment>
          ) : null}
          {assignmeButton ? (
            <BootstrapTooltip title="Assign To Myself">
              <IconButton
                type="button"
                aria-label="AssignToMyself"
                onClick={onAssignmeClick}
                edge="end"
              >
                <People />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {mapsButton ? (
            <BootstrapTooltip title="Pin Address From Maps">
              <IconButton
                type="button"
                aria-label="PinAddressFromMaps"
                onClick={onMapsClick}
                edge="end"
              >
                <Map />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {searchButton ? (
            <BootstrapTooltip title="Search">
              <IconButton
                type="button"
                aria-label="Search"
                onClick={onSearchClick}
                edge="end"
              >
                <Search />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {clearButton ? (
            <BootstrapTooltip title="Clear">
              <IconButton
                type="button"
                aria-label="Clear"
                onClick={() => {
                  clearFields.forEach((field, index) => {
                    setFieldValue(field.field, field.clearValue);
                  });
                  if (onClearFields) {
                    onClearFields();
                  }
                }}
                edge="end"
              >
                <Cancel />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
          {percentButton ? (
            <BootstrapTooltip title="Input By Percentage">
              <IconButton
                type="button"
                aria-label="InputByPercentage"
                onClick={onPercentClick}
                edge="end"
              >
                <Percent />
              </IconButton>
            </BootstrapTooltip>
          ) : null}

          {amountButton ? (
            <BootstrapTooltip title="Input By Amount">
              <IconButton
                type="button"
                aria-label="InputByAmount"
                onClick={onAmountClick}
                edge="end"
              >
                <AttachMoney />
              </IconButton>
            </BootstrapTooltip>
          ) : null}
        </InputAdornment>
      ) : null,
    startAdornment:
      currencyField || iconField ? (
        <InputAdornment position="start">
          {iconField && iconOnStart !== "" ? (
            <React.Fragment>
              {iconOnStartTooltip !== "" ? (
                <BootstrapTooltip title={iconOnStartTooltip}>
                  <i
                    className={`${iconOnStart}${
                      iconOnStartColor !== "" ? ` text-${iconOnStartColor}` : ``
                    }`}
                  ></i>
                </BootstrapTooltip>
              ) : (
                <i
                  className={`${iconOnStart}${
                    iconOnStartColor !== "" ? ` text-${iconOnStartColor}` : ``
                  }`}
                ></i>
              )}
            </React.Fragment>
          ) : null}
          {currencyField ? `${currencyCode}` : null}
        </InputAdornment>
      ) : null,
    autoFocus,
    readOnly: readonly,
  };
  if (currencyField || percentageField) {
    inputProps.inputComponent = CurrencyFormat;
  }
  return (
    <>
      <TextField
        id={fieldName}
        type={quantityField ? "number" : "text"}
        label={label}
        value={value ? value : fieldValue ?? ""}
        variant={variant}
        disabled={disabled}
        size={size}
        margin={margin}
        style={{ width: "100%", ...style }}
        onBlur={(e) => {
          handleBlur(e);
          if (onBlur) {
            onBlur(e.target.value);
          }
        }}
        onChange={(e) => {
          if (quantityField) {
            if (isEmpty(e.target.value)) {
              e.target.value = 0;
            }
            e.target.value = parseFloat(e.target.value);
          }
          setFieldValue(fieldName, e.target.value);
          if (onChangeValue) {
            onChangeValue(e.target.value);
          }
        }}
        helperText={touchedProperty && errorsProperty}
        error={Boolean(touchedProperty && errorsProperty)}
        InputProps={inputProps}
        InputLabelProps={{ shrink: true }}
        inputProps={
          currencyField
            ? {
                style: {
                  textAlign: "right",
                },
              }
            : {}
        }
        placeholder={placeholder}
      />
    </>
  );
};

export const FormArraySelectField = ({
  label = "",
  arrayPropName = "",
  arrayPropName2 = "",
  propName = "",
  index = 0,
  index2 = 0,
  margin = "normal",
  options = [],
  onSelectChange,
}) => {
  const { values, touched, errors, setFieldValue } = useFormikContext();
  let fieldValue = null;
  let fieldName = null;
  if (arrayPropName2 === "") {
    fieldValue = values[arrayPropName][index][propName];
    fieldName = `${arrayPropName}[${index}].${propName}`;
  } else {
    fieldValue = values[arrayPropName][index][arrayPropName2][index2][propName];
    fieldName = `${arrayPropName}[${index}].${arrayPropName2}[${index2}].${propName}`;
  }
  const touchedProperty = getIn(touched, fieldName);
  const errorsProperty = getIn(errors, fieldName);
  const handleChange = (event) => {
    setFieldValue(fieldName, event.target.value);
    if (onSelectChange) {
      onSelectChange(event.target.value);
    }
  };
  return (
    <FormControl
      hiddenLabel={false}
      variant="outlined"
      size="small"
      margin={margin}
      fullWidth
      error={touchedProperty && errorsProperty}
    >
      {label !== "" ? (
        <InputLabel id={arrayPropName}>{label}</InputLabel>
      ) : null}
      <Select
        label={label}
        labelId={fieldName}
        id={fieldName}
        value={fieldValue ?? ""}
        onChange={handleChange}
      >
        {options.map((option, index) => (
          <MenuItem key={index} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const FormSelectField = ({
  label = "",
  name = "",
  secondName = "",
  margin = "normal",
  options,
  onSelectChange,
}) => {
  const {
    values,
    touched,
    errors,
    handleBlur,
    setFieldValue,
  } = useFormikContext();
  let fieldName = "";
  let fieldValue = "";
  if (secondName !== "") {
    fieldValue = values[name][secondName];
    fieldName = `${name}[${secondName}]`;
  } else {
    fieldValue = values[name];
    fieldName = name;
  }

  const touchedProperty = getIn(touched, fieldName);
  const errorsProperty = getIn(errors, fieldName);
  const handleChangeValue = (e) => {
    setFieldValue(fieldName, e.target.value);
    if (onSelectChange) {
      onSelectChange(e.target.value);
    }
  };

  return (
    <FormControl
      variant="outlined"
      size="small"
      margin={margin}
      fullWidth
      error={Boolean(touchedProperty && errorsProperty)}
    >
      <InputLabel id={name}>{label}</InputLabel>
      <Select
        labelId={name}
        id={name}
        //value={secondName === "" ? values[name] : values[name][secondName]}
        value={fieldValue}
        label={label}
        onChange={handleChangeValue}
        onBlur={handleBlur}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {Boolean(touchedProperty && errorsProperty) ? (
        <FormHelperText>{errorsProperty}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

export const FormOutlinedNoMarginTextField = (props) => {
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="none"
            fullWidth
            disabled
            variant="outlined"
            size="small"
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export const FormOutlinedTextField = (props) => {
  return (
    <React.Fragment>
      {typeof value === "undefined" ? (
        <React.Fragment>
          <TextField
            {...props}
            margin="normal"
            fullWidth
            disabled
            variant="outlined"
            size="small"
          />
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

const DateFormat = ({ field, form, ...other }) => {
  return (
    <KeyboardDatePicker
      autoOk
      disablePast={other.disablePast}
      disableFuture={other.disableFuture}
      inputVariant="outlined"
      variant="inline"
      name={field.name}
      value={field.value}
      onBlur={field.onBlur}
      format="yyyy-MM-dd"
      helperText={other.helperText}
      error={other.error}
      onChange={(date) => {
        form.setFieldValue(field.name, date, true);
      }}
      {...other}
    />
  );
};

export function CurrencyFormat(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        if (isEmpty(values.value)) {
          values.value = 0;
        }
        onChange({
          target: {
            value: parseFloat(values.value),
          },
        });
      }}
      decimalScale={2}
      thousandSeparator
      isNumericString
      prefix=""
    />
  );
}

export function CurrencyFormatFullDecimals(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        if (isEmpty(values.value)) {
          values.value = 0;
        }
        onChange({
          target: {
            value: parseFloat(values.value),
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix=""
    />
  );
}

export function QuantityFormat(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        if (isEmpty(values.value)) {
          values.value = 0;
        }
        onChange({
          target: {
            value: parseFloat(values.value),
          },
        });
      }}
      isNumericString
    />
  );
}
